import { useRef } from 'react';

export enum ProgressStepStatus {
	OK = 'OK',
	FAIL = 'FAIL',
	PENDING = 'PENDING',
}

export type TProgressStep = {
	id: number;
	status: ProgressStepStatus;
};

type ProgressMode = 'chained' | 'free';

interface IUseProgressSteps {
	<Mode extends ProgressMode = 'chained'>(
		stepsCount: number,
		mode?: Mode,
	): {
		changeStepStatus: (stepId: number, status: ProgressStepStatus) => void;
		currentStepRef: React.MutableRefObject<Mode extends 'chained' ? TProgressStep : null>;
		steps: React.MutableRefObject<TProgressStep[]>;
	};
}

export const useProgressSteps: IUseProgressSteps = <Mode extends ProgressMode = 'chained'>(
	stepsCount: number,
	mode: Mode = 'chained' as Mode,
) => {
	if (!Number.isInteger(stepsCount) || stepsCount <= 0) {
		throw new Error('stepsCount must be a positive integer');
	}

	const steps = useRef<TProgressStep[]>(
		Array.from({ length: stepsCount }).map((_count, index) => ({
			id: index + 1,
			status: ProgressStepStatus.PENDING,
		})),
	);

	const currentStepReference = useRef<Mode extends 'chained' ? TProgressStep : null>(
		mode === 'chained'
			? (steps.current[0] as Mode extends 'chained' ? TProgressStep : never)
			: (null as Mode extends 'chained' ? never : null),
	);

	const changeStepStatus = (stepId: number, status: ProgressStepStatus) => {
		const stepIndex = steps.current.findIndex((s) => s.id === stepId);

		if (stepIndex === -1) return;

		if (
			mode === 'chained' &&
			stepIndex !== 0 &&
			steps.current.slice(0, stepIndex).some((s) => s.status !== ProgressStepStatus.OK)
		) {
			return;
		}

		steps.current = steps.current.map((step) => {
			return step.id === stepId ? { ...step, status } : step;
		});

		const currentStepIndex =
			status === ProgressStepStatus.OK && stepIndex + 1 !== steps.current.length ? stepIndex + 1 : stepIndex;

		currentStepReference.current = steps.current[currentStepIndex] as Mode extends 'chained' ? TProgressStep : never;
	};

	return {
		changeStepStatus,
		currentStepRef: currentStepReference,
		steps,
	};
};
